// Acquisitions data + types for the Atlas prototype.
//
// Backing data is a static fixture (src/_fixtures/acquisitions.json) dumped
// from sandbox_poc.acquisitions. Refresh with:
//   bash C:/Dev/OptiqHQ/web-proto/_tools/refresh_fixture.sh
//
// When the module set is finalized, promote to a real /api/hq/acquisitions/*
// endpoint on the OptiqOS API and swap the loader.

import fixture from '../_fixtures/acquisitions.json';
import { authFetch } from './authFetch';

// Live API base (mirrors api.ts). The acquisitions read endpoints live on the
// OptiqOS API at /api/hq/acquisitions/* over sandbox_poc.acquisitions.
const API_BASE = import.meta.env.VITE_HQ_API_BASE ?? 'https://api.optiq-os.com';

export type WorkflowStage = {
  stage_key: string;
  stage_order: number;
  display_name: string;
  default_owner: string | null;
  sla_hours: number | null;
  is_terminal: boolean;
};

export type DealListing = {
  listing_id: string;
  source_listing_id: string | null;
  source_platform: string | null;
  business_name: string;
  business_type: string | null;
  vertical: string | null;
  location_city: string | null;
  location_state: string | null;
  region: string | null;
  asking_price: number | null;
  revenue: number | null;
  cash_flow: number | null;
  ebitda: number | null;
  employees: number | null;
  year_established: number | null;
  listing_url: string | null;
  description: string | null;
  delivery_model: string | null;
  reason_for_sale: string | null;
  lease_terms: string | null;
  industries_text: string | null;
  owner_terms: string | null;
  client_count: number | null;
};

export type DealBroker = {
  broker_id: string;
  company_name: string;
  broker_name: string | null;
  email: string | null;
  phone: string | null;
  website: string | null;
  platform: string | null;
  relationship_status: string | null;
} | null;

export type DealSummary = {
  cim_count: number;
  nda_doc_count: number;
  call_count: number;
  email_count: number;
  cim_on_disk: boolean | null;
  last_activity_type: string | null;
  last_outcome: string | null;
  last_summary: string | null;
  last_touched_at: string | null;
  last_outbound_call: string | null;
} | null;

export type DealActivity = {
  activity_id: string;
  activity_type: string;
  direction: string | null;
  owner: string;
  counterparty_name: string | null;
  counterparty_email: string | null;
  outcome: string | null;
  completed_at: string | null;
  duration_min: number | null;
  summary: string | null;
  next_action: string | null;
  next_action_due: string | null;
};

export type DealDocument = {
  doc_id: string;
  document_type: string;
  document_name: string | null;
  storage_url: string | null;
  received_date: string | null;
  notes: string | null;
};

export type DealContact = {
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  role: string | null;
};

export type DealTask = {
  task_id: string;
  task_kind: string;
  owner: string;
  priority: string | null;
  due_at: string | null;
  urgency: string | null;
  summary: string;
  source_stage: string | null;
};

export type CimExtract = {
  extract_id: string;
  cim_year: number | null;
  is_year_summary: boolean;
  gross_revenue: number | null;
  net_revenue: number | null;
  cash_flow: number | null;
  ebitda: number | null;
  owner_w2: number | null;
  payroll_total: number | null;
  gross_margin_pct: number | null;
  asking_price: number | null;
  asking_multiple_rev: number | null;
  asking_multiple_sde: number | null;
  service_mix: Record<string, number> | null;
  client_mix: Record<string, number> | null;
  client_concentration: Record<string, number> | null;
  delivery_model: string | null;
  locations_count: number | null;
  staff_breakdown: Record<string, unknown> | null;
  tech_stack: Record<string, string[]> | null;
  reason_for_sale_tag: string | null;
  extraction_method: string;
  extraction_confidence: 'high' | 'medium' | 'low' | 'failed';
  fields_missing: string[] | null;
};

export type Deal = {
  deal_id: string;
  stage: string;
  stage_substatus: string | null;
  priority: string;
  next_action: string | null;
  next_action_owner: string | null;
  inquiry_date: string | null;
  nda_signed_date: string | null;
  cim_received_date: string | null;
  meeting_date: string | null;
  ioi_date: string | null;
  loi_date: string | null;
  created_at: string;
  updated_at: string;
  notes: string | null;
  listing: DealListing;
  broker: DealBroker;
  summary: DealSummary;
  activities: DealActivity[];
  documents: DealDocument[];
  contacts: DealContact[];
  open_tasks: DealTask[];
  cim_extracts: CimExtract[];
};

export type AcquisitionsFixture = {
  as_of: string;
  workflow_stages: WorkflowStage[];
  deals: Deal[];
};

const data = fixture as AcquisitionsFixture;

export const acquisitions = {
  asOf: data.as_of,
  stages: data.workflow_stages,
  deals: data.deals,
  byId(dealId: string): Deal | undefined {
    return data.deals.find((d) => d.deal_id === dealId);
  },
  stageByKey(stageKey: string): WorkflowStage | undefined {
    return data.workflow_stages.find((s) => s.stage_key === stageKey);
  },
};

// @section live Call-queue loader — live API with graceful fixture fallback.
// One flat row per open broker call (the /call-queue endpoint shape). When the
// API is unreachable / not deployed (503/404/CORS) we derive the same rows from
// the static fixture so the screen still renders, same pattern as the
// time-value / campaigns fallbacks in api.ts.

// Canonical urgency buckets from acquisitions.v_call_queue.
export type AcqUrgency = 'overdue' | 'today' | 'soon' | 'later' | 'undated';

export type AcqCallRow = {
  activity_id: string | null;
  owner: string | null;
  next_action_due: string | null;
  urgency: AcqUrgency | string | null;
  next_action: string | null;
  last_activity_type: string | null;
  last_outcome: string | null;
  last_summary: string | null;
  last_touched_at: string | null;
  broker_company: string | null;
  counterparty_name: string | null;
  counterparty_email: string | null;
  counterparty_phone: string | null;
  source_listing_id: string | null;
  listing_name: string | null;
  listing_state: string | null;
  asking_price: number | null;
  revenue: number | null;
  deal_stage: string | null;
  deal_id: string | null;
  broker_id: string | null;
  listing_id: string | null;
};

// Fixture urgency keys (due_today/due_soon/future) → canonical API buckets.
function normalizeUrgency(u: string | null | undefined): AcqUrgency {
  switch (u) {
    case 'overdue': return 'overdue';
    case 'today': case 'due_today': return 'today';
    case 'soon': case 'due_soon': return 'soon';
    case 'later': case 'future': return 'later';
    default: return 'undated';
  }
}

function deriveCallRowsFromFixture(owner: string): AcqCallRow[] {
  const rows: AcqCallRow[] = [];
  for (const d of data.deals) {
    for (const t of d.open_tasks ?? []) {
      if (t.task_kind !== 'call_broker') continue;
      if (owner !== 'all' && t.owner?.toLowerCase() !== owner.toLowerCase()) continue;
      rows.push({
        activity_id: t.task_id,
        owner: t.owner,
        next_action_due: t.due_at,
        urgency: normalizeUrgency(t.urgency),
        next_action: t.summary,
        last_activity_type: d.summary?.last_activity_type ?? null,
        last_outcome: d.summary?.last_outcome ?? null,
        last_summary: d.summary?.last_summary ?? null,
        last_touched_at: d.summary?.last_touched_at ?? null,
        broker_company: d.broker?.company_name ?? null,
        counterparty_name: d.broker?.broker_name ?? null,
        counterparty_email: d.broker?.email ?? null,
        counterparty_phone: d.broker?.phone ?? null,
        source_listing_id: d.listing.source_listing_id,
        listing_name: d.listing.business_name,
        listing_state: d.listing.location_state,
        asking_price: d.listing.asking_price,
        revenue: d.listing.revenue,
        deal_stage: d.stage,
        deal_id: d.deal_id,
        broker_id: d.broker?.broker_id ?? null,
        listing_id: d.listing.listing_id,
      });
    }
  }
  return rows;
}

export async function fetchCallQueue(
  owner = 'patrick',
): Promise<{ rows: AcqCallRow[]; source: 'api' | 'fixture' }> {
  try {
    const r = await authFetch(`${API_BASE}/api/hq/acquisitions/call-queue?owner=${encodeURIComponent(owner)}`);
    if (r.ok) {
      const j = await r.json();
      return { rows: (j.items ?? []) as AcqCallRow[], source: 'api' };
    }
    // 503 (SandboxPocDb unwired) / 404 (not deployed) → fixture
  } catch {
    // network / CORS → fixture
  }
  return { rows: deriveCallRowsFromFixture(owner), source: 'fixture' };
}

// @section live2 Pipeline loader — full fixture-shape dump from the live API,
// with graceful fallback to the static fixture when the endpoint is unreachable
// (503 SandboxPocDb unwired / 404 not deployed / CORS / network). Same pattern
// as fetchCallQueue above and the time-value / campaigns fallbacks in api.ts.
export async function fetchPipeline(): Promise<{
  data: AcquisitionsFixture;
  source: 'api' | 'fixture';
}> {
  try {
    const r = await authFetch(`${API_BASE}/api/hq/acquisitions/pipeline`);
    if (r.ok) {
      const j = (await r.json()) as AcquisitionsFixture;
      // Guard against an empty/`null deals` payload (e.g. SandboxPocDb returned
      // the default placeholder) — fall back to the fixture so the board never
      // renders blank.
      if (j && Array.isArray(j.deals) && j.deals.length > 0) {
        return { data: j, source: 'api' };
      }
    }
  } catch {
    // network / CORS → fixture
  }
  return { data, source: 'fixture' };
}

export const VERTICAL_LABELS: Record<string, string> = {
  cpa_tax_accounting: 'CPA / Tax',
  cpa: 'CPA',
  plumbing: 'Plumbing',
  hvac_mechanical: 'HVAC',
  staffing: 'Staffing',
  mixed: 'Mixed',
  other: 'Other',
};

export function fmtUsd(n: number | null | undefined): string {
  if (n == null) return '—';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `$${Math.round(n / 1_000)}K`;
  return `$${n.toFixed(0)}`;
}

export function fmtDate(s: string | null | undefined): string {
  if (!s) return '—';
  const d = new Date(s);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
}

export function fmtDays(s: string | null | undefined, asOf?: string): string {
  if (!s) return '—';
  const d = new Date(s);
  const ref = asOf ? new Date(asOf) : new Date();
  const days = Math.floor((ref.getTime() - d.getTime()) / 86400000);
  if (days === 0) return 'today';
  if (days === 1) return '1d ago';
  if (days < 0) return `in ${-days}d`;
  return `${days}d ago`;
}
